import { containsInvariant } from "./filter-util";

export class PasswordValidatorService {
    constructor($http, $q, apexApiUrl) {
        'ngInject';
        this.$http = $http;
        this.$q = $q;
        this.apexApiUrl = apexApiUrl;
        this.cachedValidator = null;
    }

    /**
     * Retrieve a password validator for a particular user.
     */
    retrievePasswordValidator(user) {
        if (this.cachedValidator) {
            return this.$q.resolve(this.cachedValidator);
        }
        return this.$http({
            method: "GET",
            url: `${this.apexApiUrl}/home/password-validator`,
            disableApexAuthInterceptor: true
        }).then(response => mapRules(response.data, user))
            .then(rules => this.cachedValidator = new PasswordValidator(rules));
    }
}

/**
 * An abstract password validation rule that can be required.
 */
export class PasswordValidationRule {
    /**
     * Initialize a password validation rule with base abstract logic.
     * @param {boolean} required Indicate if the rule is required. If not required, the
     * rule can still be satisfied to represent increased security, but it will not be enforced.
     */
    constructor(required) {
        this._required = required;
    }

    isRequired() {
        return !!this._required;
    }

    isSatisfiedBy(password) {
        throw new Error("isSatisfiedBy must be implemented by a subclass");
    }
}

/**
 * A rule that a password cannot contain the user's first or last name.
 */
export class CheckPasswordContainsNameRule extends PasswordValidationRule {
    /**
     * Initialize the rule for checking if a user's password contains their name.
     * @param {boolean} required Indicates if the rule is required.
     * @param {{FirstName: string, LastName: string}} user A user with a first and last name.
     * @param {number} minimumNameLength Skip validation if the user's name is below a certain length.
     */
    constructor(required, user, minimumNameLength) {
        super(required)
        this.user = user;
        this.minimumNameLength = minimumNameLength;
        this.description = `Cannot contain the user's first or last names`;
    }

    /**
     * Return true if the user's password does not contain the user's name.
     * @param {string} password The user's desired password
     */
    isSatisfiedBy(password) {
        const names = [this.user.FirstName, this.user.LastName]
            .filter(p => (this.minimumNameLength || this.minimumNameLength === 0)
                && (p || "").length > this.minimumNameLength);
        return names.reduce((all, name) => all && !containsInvariant(password, name || ""), true);
    }
}

/**
 * A rule that a password must contain a digit.
 */
export class RequireDigitRule extends PasswordValidationRule {
    constructor(required) {
        super(required);
        this.description = "At least one digit (0-9)";
    }

    isSatisfiedBy(password) {
        return /[0-9]/.test(password);
    }
}

/**
 * A rule that a password must contain an uppercase letter.
 */
export class RequireUppercaseRule extends PasswordValidationRule {
    constructor(required) {
        super(required);
        this.description = "At least one uppercase letter (A-Z)";
    }
    isSatisfiedBy(password) {
        return /[A-Z]/.test(password);
    }
}

export class RequireLowercaseRule extends PasswordValidationRule {
    constructor(required) {
        super(required);
        this.description = "At least one lowercase letter (a-z)";
    }
    isSatisfiedBy(password) {
        return /[a-z]/.test(password);
    }
}

export class RequireSpecialCharacterRule extends PasswordValidationRule {
    constructor(required) {
        super(required);
        this.description = "At least one special character (non letter or number)";
    }
    isSatisfiedBy(password) {
        return /[^a-zA-Z0-9]/.test(password);
    }
}

export class MinLengthRule extends PasswordValidationRule {
    constructor(required, length) {
        super(required);
        this.length = length;
        this.description = `At least ${this.length} characters long`;
    }

    isSatisfiedBy(password) {
        return (password || "").length >= this.length;
    }
}

export class MaxLengthRule extends PasswordValidationRule {
    constructor(required, length) {
        super(required);
        this.length = length;
        this.description = `${this.length} or fewer characters long`;
    }

    isSatisfiedBy(password) {
        return (password || "").length <= this.length;
    }
}

function mapRules(rules, user) {
    const validationRules = [];
    // The following two rules are inherently satisfied by a blank password, so display them at the top.
    if (user) {
        validationRules.push(new CheckPasswordContainsNameRule(rules.CheckPasswordContainName,
            user, rules.MinValToCheckName));
    }
    if (rules.MaxLength) {
        validationRules.push(new MaxLengthRule(true, rules.MaxLength))
    }
    validationRules.push(new MinLengthRule(true, rules.RequiredLength));
    validationRules.push(new RequireDigitRule(rules.RequireDigit));
    validationRules.push(new RequireLowercaseRule(rules.RequireLowercase));
    validationRules.push(new RequireUppercaseRule(rules.RequireUppercase));
    validationRules.push(new RequireSpecialCharacterRule(rules.RequireNonLetterOrDigit));

    // Also push a hidden "minlength=maxlength" to make passwords at the max length considered most secure
    validationRules.push(new MinLengthRule(false, rules.MaxLength || rules.RequiredLength));
    return validationRules;
}

export class PasswordValidator {
    constructor(validationRules) {
        this.validationRules = validationRules;
    }

    validate(password) {
        return this.validationRules.reduce((all, rule) => all && (!rule.isRequired()
            || rule.isSatisfiedBy(password)), true);
    }

    calculateStrength(password) {
        // By default, if no rules are specified, scale strength based on length.
        return this.validationRules.length === 0
            ? Math.min(1, password.length / 12) // Consider 12 characters as "strongest"
            : this.validationRules.reduce((count, rule) => count
                + (rule.isSatisfiedBy(password || "") ? 1 : 0), 0)
                / this.validationRules.length;
    }
}
